import dayjs from 'dayjs'
import React from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { setCustomerValuesVendorSlice, setCustomerValuesStromlieferantNameSlice } from '../../../store/slices/customerInputValuesSlice'
import { customerValuesType } from '../../../types'
import {
  FormDate,
  FormCheck,
  FormInput,
  FormSelectSearchable,
  FormRadioItem,
  AddressWithValidation
} from '../../molecules'

type VendorProps = {
  nextPage: string;
  prevPage: string;
  changeActivePage: (pageUrl: string) => void
}

const Component = ({ nextPage, prevPage, changeActivePage }: VendorProps) => {
  const customerInputValues = useAppSelector(state => state.customervalues)
  const dispatch = useAppDispatch()

  const defaultFormValues = { ...customerInputValues['p-vendor'] }
  defaultFormValues['vendor-waerme-vorjahresstromverbrauch'] =
    customerInputValues['p-intro'] ? customerInputValues['p-intro']['power-consumption-year-kwh'] : ''
  defaultFormValues['vendor-wae-vorjahresstromverbrauch'] =
    customerInputValues['p-intro'] ? customerInputValues['p-intro']['intro-wae-vorjahresstromverbrauch'] : ''
  defaultFormValues['vendor-faf-1-vorjahresstromverbrauch'] =
    customerInputValues['p-intro'] ? customerInputValues['p-intro']['intro-faf-1-vorjahresstromverbrauch'] : ''
  defaultFormValues['vendor-faf-2-vorjahresstromverbrauch'] =
    customerInputValues['p-intro'] ? customerInputValues['p-intro']['intro-faf-2-vorjahresstromverbrauch'] : ''

  defaultFormValues['vendor-faf-1-postleitzahl'] =
    customerInputValues['p-intro'] ? customerInputValues['p-intro']['intro-faf-1-postleitzahl'] : ''
  defaultFormValues['vendor-faf-2-postleitzahl'] =
    customerInputValues['p-intro'] ? customerInputValues['p-intro']['intro-faf-2-postleitzahl'] : ''

  const { control, watch, register, handleSubmit, getValues, formState: { errors }, clearErrors, setValue } = useForm<customerValuesType.Vendor>({
    mode: 'onChange',
    defaultValues: defaultFormValues
  })
  const energyProviders:Array<string> = drupalSettings.app_block.energy_providers

  // Build correct structure for formSelectSearchable options so that react-select can work with it.
  const selectOptions: Array<{ label: string, value: number }> = []
  energyProviders.map(
    (energyProvider, index) => {
      selectOptions.push({
        label: energyProvider,
        value: index
      })
      return false
    }
  )

  const watchAdditionalInputs = watch('vendor-waerme-kuendigungSonder', false)
  const watchWaeAdditionalInputs = watch('vendor-wae-kuendigungSonder')
  const watchFaf1AdditionalInputs = watch('vendor-faf-1-kuendigungSonder')
  const watchFaf2AdditionalInputs = watch('vendor-faf-2-kuendigungSonder')
  const watchRadioItems = watch('vendor-waerme-wechselgrund')
  const watchWaeRadioItems = watch('vendor-wae-wechselgrund')
  const watchFaf1RadioItems = watch('vendor-faf-1-wechselgrund')
  const watchFaf2RadioItems = watch('vendor-faf-2-wechselgrund')
  const watchWaermeKuendigung = watch('vendor-waerme-kuendigungSonderDatum')
  const watchWaermeEingezogen = watch('vendor-waerme-neuEingezogenDatum')
  const watchFaf1Kuendigung = watch('vendor-faf-1-kuendigungSonderDatum')
  const watchFaf1Eingezogen = watch('vendor-faf-1-neuEingezogenDatum')
  const watchFaf2Kuendigung = watch('vendor-faf-2-kuendigungSonderDatum')
  const watchFaf2Eingezogen = watch('vendor-faf-2-neuEingezogenDatum')
  const watchWaeKuendigung = watch('vendor-wae-kuendigungSonderDatum')
  const watchWaeEingezogen = watch('vendor-wae-neuEingezogenDatum')

  const handleBack = () => {
    const formData = getValues()
    dispatch(setCustomerValuesVendorSlice(formData))
    changeActivePage(prevPage)
  }

  /**
   * @param data
   */
  const onSubmitFn: SubmitHandler<customerValuesType.Vendor> = (data) => {
    dispatch(setCustomerValuesVendorSlice(data))

    const energyProviderValues: customerValuesType.StromlieferantName = {
      lichtstrom: data['vendor-waerme-anbieter']
        ? energyProviders[data['vendor-waerme-anbieter'].value]
        : '',
      waermestrom: data['vendor-wae-anbieter']
        ? energyProviders[data['vendor-wae-anbieter'].value]
        : '',
      familyAndFriends: data['vendor-faf-1-anbieter']
        ? energyProviders[data['vendor-faf-1-anbieter'].value]
        : '',
      familyAndFriends2: data['vendor-faf-2-anbieter']
        ? energyProviders[data['vendor-faf-2-anbieter'].value]
        : ''
    }
    dispatch(setCustomerValuesStromlieferantNameSlice(energyProviderValues))

    changeActivePage(nextPage)
  }

  const packageWaerme =
    customerInputValues['p-intro'] &&
    customerInputValues['p-intro']['intro-wae-waermePaket']

  const packageFaF =
    customerInputValues['p-intro'] &&
    customerInputValues['p-intro']['intro-faf-paketFamilyAndFriends']

  const packageFaF2Details =
    customerInputValues['p-intro'] &&
    customerInputValues['p-intro']['intro-faf-2-details']

  return (
    <div className="react-form__page react-form__page--vendor">
      <div className="react-form__page__header relative pt-10">
        <div className="react-form__page__intro-text text-center pb-10">
          <h1 className="h2">Ihr Wechsel</h1>
          <p>
            Damit wir Ihren Wechsel in die SENEC.Cloud umsetzen können, benötigen wir die nachfolgenden Angaben.
            Diese verwenden wir für die Anmeldung von SENEC als Ihren neuen Stromlieferanten.
          </p>
        </div>
      </div>

      <div className="react-form__page--intro__body">
        <form>
          <div className="bg-gray-lighter p-6 md:p-10 xl:p-16">
            <div className="flex flex-wrap -mx-4">
              <FormSelectSearchable
                errors={errors}
                register={register}
                control={control}
                id="vendor-waerme-anbieter"
                label="Bisheriger Stromlieferant"
                placeholder="Auswählen..."
                info="Bitte nennen Sie uns Ihren bisherigen Stromanbieter.
                Für die Auswahl eines Energieversorgers aus der Liste können Sie einfach
                dessen Anfangsbuchstaben eingeben."
                className="w-full sm:w-1/2 px-4"
                options={selectOptions}
                isSearchable={true}
                validations={{
                  required: 'Bitte füllen Sie dieses Feld aus.'
                }}
              />
              <FormInput
                id="vendor-waerme-vorjahresstromverbrauch"
                label="Stromverbrauch"
                measurement="kWh"
                disabled={true}
                info="Hier haben wir Ihren angegebenen Stromverbrauch hinterlegt."
                className="w-full sm:w-1/2 px-4"
                errors={errors}
                register={register}
              />
              <FormInput
                id="vendor-waerme-zaehlernummer"
                label="Zählernummer"
                info="Sie finden die Zählernummer Ihres Stromzählers auf der Jahresabrechnung des Energieversorgers."
                className="w-full sm:w-1/2 px-4"
                errors={errors}
                register={register}
                validations={{
                  minLength: {
                    value: 5,
                    message: 'Die Mindestanzahl an Zeichen beträgt 5'
                  },
                  maxLength: {
                    value: 16,
                    message: 'Die maximale Zeichenanzahl beträgt 16'
                  },
                  pattern: {
                    value: /^((?!kommt$)(?!folgt$)(?!später$)(?!nachreichen$))([a-zA-Z0-9äöüÄÖÜß-])*$/,
                    message: 'Bitte stellen Sie sicher, dass es sich bei Ihrer Eingabe um eine Zählernummer handelt.'
                  },
                  validate: {
                    validateSerial: (value) => {
                      const marktlokationsId = watch('marktlokations-identifikationsnummer')
                      if (!value && !marktlokationsId) {
                        return 'Bitte geben Sie entweder die Zählernummer oder die Marktstandort-ID an.'
                      }
                      const waeZaehlernummer = watch('vendor-wae-zaehlernummer')
                      const faf1Zaehlernummer = watch('vendor-faf-1-zaehlernummer')
                      const faf2Zaehlernummer = watch('vendor-faf-2-zaehlernummer')
                      if (value && (value === waeZaehlernummer || value === faf1Zaehlernummer || value === faf2Zaehlernummer)) {
                        return 'Bitte überprüfen Sie Ihre Angabe. Die Zählernummer wurde bereits innerhalb der Bestellung von Ihnen verwendet. Bitte stellen Sie sicher, dass jede Zählernummer nur einmalig im Bestellprozess angegeben wird.'
                      }

                      return true
                    }
                  }
                }}
              />
              <FormInput
                id="marktlokations-identifikationsnummer"
                label="Marktlokations-ID"
                info="Sie finden die Marktlokations-ID in Ihrer letzten Rechnung.
                Sie dient der eindeutigen Zuordnung von Abnahmestellen, macht den Vertragswechsel eindeutig und damit für Sie schneller."
                className="w-full sm:w-1/2 px-4"
                errors={errors}
                register={register}
                validations={{
                  minLength: {
                    value: 11,
                    message: 'Bitte tragen Sie Ihre 11-stellige Marktlokations-ID ein.'
                  },
                  maxLength: {
                    value: 11,
                    message: 'Bitte tragen Sie Ihre 11-stellige Marktlokations-ID ein.'
                  },
                  pattern: {
                    value: /^\d+$/,
                    message: 'Bitte geben Sie für die Marktlokations-ID nur Zahlen ein.'
                  },
                  validate: {
                    validateSerial: (value) => {
                      const zaehlernummer = watch('vendor-waerme-zaehlernummer')
                      if (!value && !zaehlernummer) {
                        return 'Bitte geben Sie entweder die Zählernummer oder die Marktstandort-ID an.'
                      }
                      return true
                    }
                  }
                }}
              />
              <div
                className="react-form__form-group form-group form-group--reduced-spacing form-group--no-outline w-full px-4">
                <h2 className="h4">Wechselgrund</h2>
                <FormRadioItem
                  name="vendor-waerme-wechselgrund"
                  id="vendor-waerme-neuEingezogen"
                  label="Ich werde umziehen."
                  value="vendor-waerme-neuEingezogen"
                  className="inline-block"
                  register={register}
                  errors={errors}
                  defaultChecked={customerInputValues['p-vendor']['vendor-waerme-wechselgrund'] === 'vendor-waerme-neuEingezogen'}
                />
                {watchRadioItems === 'vendor-waerme-neuEingezogen' && (
                  <div className="w-full">
                    <div className="flex flex-wrap">
                      <FormDate
                        errors={errors}
                        register={register}
                        id="vendor-waerme-neuEingezogenDatum"
                        control={control}
                        label="Datum des Einzugs"
                        className="w-full sm:w-1/2"
                        peekNextMonth={true}
                        showMonthDropdown={true}
                        showYearDropdown={true}
                        dropdownMode="select"
                        validations={{
                          required: 'Bitte füllen Sie dieses Feld aus.',
                          validate: value => {
                            if (dayjs(watchWaermeEingezogen).isBefore(dayjs())) {
                              return 'Aufgrund von Fristen können wir Ihren Umzug nur anmelden, wenn dieser in der Zukunft liegt. Sind Sie bereits umgezogen, wählen Sie bitte "Ich möchte den Lieferanten wechseln aus".'
                            }
                            return true
                          }
                        }}
                      />
                    </div>
                  </div>
                )}
                <FormRadioItem
                  name="vendor-waerme-wechselgrund"
                  id="vendor-waerme-lieferantwechsel"
                  label="Ich möchte den Lieferanten wechseln."
                  register={register}
                  errors={errors}
                  defaultChecked={customerInputValues['p-vendor']['vendor-waerme-wechselgrund'] === 'vendor-waerme-lieferantwechsel'}
                />
                {watchRadioItems === 'vendor-waerme-lieferantwechsel' && (
                  <div className="w-full">
                    <div className="flex flex-wrap">
                      <FormCheck
                        id="vendor-waerme-kuendigungSonder"
                        label="Ich habe bereits bei meinem bisherigen Stromlieferanten gekündigt."
                        className="w-full pt-4 px-4"
                        register={register}
                        errors={errors}
                        defaultChecked={false}
                      />
                      {watchAdditionalInputs && (
                        <div className="w-full px-4">
                          <div className="flex flex-wrap -mx-4">
                            <FormInput
                              errors={errors}
                              id="vendor-waerme-kuendigungSonder-vertragspartner"
                              label="Bisheriger Kundenname"
                              className="w-full sm:w-1/2 px-4"
                              register={register}
                              validations={{
                                required: 'Bitte füllen Sie dieses Feld aus.'
                              }}
                            />
                            <FormDate
                              errors={errors}
                              customMessage={(dayjs(watchWaermeKuendigung).isBefore(dayjs()))
                                ? 'Aufgrund von Fristen ist ein rückwirkender Wechsel nicht möglich. Wir werden Sie schnellstmöglich in Belieferung bringen.'
                                : ''
                              }
                              register={register}
                              id="vendor-waerme-kuendigungSonderDatum"
                              control={control}
                              label="Gekündigt zum"
                              className="w-full sm:w-1/2 px-4"
                              peekNextMonth={true}
                              showMonthDropdown={true}
                              showYearDropdown={true}
                              dropdownMode="select"
                              validations={{
                                required: 'Bitte füllen Sie dieses Feld aus.'
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {packageFaF && (
            <div
              className={`p-6 md:p-10 xl:p-16 ${
                packageWaerme
                  ? 'bg-gray-light'
                  : 'bg-gray-lighter'
              }`}
            >
              <div className="flex flex-wrap">
                <div className="w-full">
                  <h2 className="h3">Family & Friends</h2>
                  <p className="text-sm">
                    Mit dem Zusatzprodukt Family & Friends können Sie bis zu zwei weitere Lieferstellen
                    über den SENEC-Vorteilstarif versorgen. So profitieren Sie gleich mehrfach.
                  </p>
                </div>
                <div className="w-full">
                  <div className="flex flex-wrap -mx-4">
                    <div className="w-full md:w-1/2 px-4">
                      <h3 className="h5 pt-4 pb-10">
                        1. zusätzliche Abnahmestelle
                      </h3>
                      <AddressWithValidation
                        sliceName="vendorFaf1"
                        prefix="vendor-faf-1"
                        twoRows={true}
                        errors={errors}
                        register={register}
                        watch={watch}
                        control={control}
                        clearErrors={clearErrors}
                        setValue={setValue}
                        initialPostalCode={customerInputValues['p-intro']['intro-faf-1-postleitzahl']}
                        cityOptionsInitial={typeof customerInputValues['p-vendor']['vendor-faf-1-ort'] === 'object' ? [customerInputValues['p-vendor']['vendor-faf-1-ort']] : []}
                        streetOptionsInitial={typeof customerInputValues['p-vendor']['vendor-faf-1-strasse'] === 'object' ? [customerInputValues['p-vendor']['vendor-faf-1-strasse']] : []}
                      />
                      <FormSelectSearchable
                        errors={errors}
                        register={register}
                        control={control}
                        id="vendor-faf-1-anbieter"
                        label="Bisheriger Stromlieferant"
                        placeholder="Auswählen..."
                        options={selectOptions}
                        isSearchable={true}
                        validations={{
                          required: 'Bitte füllen Sie dieses Feld aus.'
                        }}
                      />
                      <FormInput
                        id="vendor-faf-1-vorname"
                        label="Vorname Anschlussnutzer"
                        errors={errors}
                        register={register}
                        validations={{
                          required: 'Bitte füllen Sie dieses Feld aus.'
                        }}
                      />
                      <FormInput
                        id="vendor-faf-1-name"
                        label="Name Anschlussnutzer"
                        errors={errors}
                        register={register}
                        validations={{
                          required: 'Bitte füllen Sie dieses Feld aus.'
                        }}
                      />
                      <div className="flex flex-wrap -mx-4">
                        <FormInput
                          id="vendor-faf-1-zaehlernummer"
                          label="Zählernummer"
                          className="w-full md:w-6/12 px-4"
                          errors={errors}
                          register={register}
                          validations={{
                            minLength: {
                              value: 5,
                              message: 'Die Mindestanzahl an Zeichen beträgt 5'
                            },
                            maxLength: {
                              value: 16,
                              message: 'Die maximale Zeichenanzahl beträgt 16'
                            },
                            pattern: {
                              value: /^((?!kommt$)(?!folgt$)(?!später$)(?!nachreichen$))([a-zA-Z0-9äöüÄÖÜß-])*$/,
                              message: 'Bitte stellen Sie sicher, dass es sich bei Ihrer Eingabe um eine Zählernummer handelt.'
                            },
                            validate: {
                              validateSerial: (value) => {
                                const marktlokationsId = watch('vendor-faf-1-marktlokations-id')
                                if (!value && !marktlokationsId) {
                                  return 'Bitte geben Sie entweder die Zählernummer oder die Marktstandort-ID an.'
                                }
                                const waeZaehlernummer = watch('vendor-wae-zaehlernummer')
                                const waermeZaehlernummer = watch('vendor-waerme-zaehlernummer')
                                const faf2Zaehlernummer = watch('vendor-faf-2-zaehlernummer')
                                if (value && (value === waermeZaehlernummer || value === waeZaehlernummer || value === faf2Zaehlernummer)) {
                                  return 'Bitte überprüfen Sie Ihre Angabe. Die Zählernummer wurde bereits innerhalb der Bestellung von Ihnen verwendet. Bitte stellen Sie sicher, dass jede Zählernummer nur einmalig im Bestellprozess angegeben wird.'
                                } else {
                                  return true
                                }
                              }
                            }
                          }}
                        />
                        <FormInput
                          id="vendor-faf-1-vorjahresstromverbrauch"
                          label="(Vorjahres-) Stromverbrauch in KWh"
                          measurement="kWh"
                          className="w-full md:w-6/12 px-4"
                          disabled={true}
                          errors={errors}
                          register={register}
                        />
                      </div>
                      <FormInput
                        id="vendor-faf-1-marktlokations-id"
                        label="Marktlokations-ID"
                        info="Sie finden die Marktlokations-ID in Ihrer letzten Rechnung.
                       Sie dient der eindeutigen Zuordnung von Abnahmestellen, macht den Vertragswechsel eindeutig und damit für Sie schneller."
                        errors={errors}
                        register={register}
                        validations={{
                          minLength: {
                            value: 11,
                            message: 'Bitte tragen Sie Ihre 11-stellige Marktlokations-ID ein.'
                          },
                          maxLength: {
                            value: 11,
                            message: 'Bitte tragen Sie Ihre 11-stellige Marktlokations-ID ein.'
                          },
                          pattern: {
                            value: /^\d+$/,
                            message: 'Bitte geben Sie für die Marktlokations-ID nur Zahlen ein.'
                          },
                          validate: {
                            validateSerial: (value) => {
                              const zaehlernummer = watch('vendor-faf-1-zaehlernummer')
                              if (!value && !zaehlernummer) {
                                return 'Bitte geben Sie entweder die Zählernummer oder die Marktstandort-ID an.'
                              }
                              return true
                            }
                          }
                        }}
                      />
                      <div
                        className="react-form__form-group form-group form-group--reduced-spacing form-group--no-outline w-full">
                        <h3 className="h4">Wechselgrund</h3>
                        <div>
                          <FormRadioItem
                            name="vendor-faf-1-wechselgrund"
                            id="vendor-faf-1-neuEingezogen"
                            label="Ich werde umziehen."
                            value="vendor-faf-1-neuEingezogen"
                            register={register}
                            errors={errors}
                            defaultChecked={customerInputValues['p-vendor']['vendor-faf-1-wechselgrund'] === 'vendor-faf-1-neuEingezogen'}
                          />
                          {watchFaf1RadioItems ===
                            'vendor-faf-1-neuEingezogen' && (
                              <div className="w-full">
                                <div className="flex flex-wrap">
                                  <FormDate
                                    errors={errors}
                                    register={register}
                                    control={control}
                                    id="vendor-faf-1-neuEingezogenDatum"
                                    label="Datum des Einzugs"
                                    className="w-full sm:w-1/2"
                                    peekNextMonth={true}
                                    showMonthDropdown={true}
                                    showYearDropdown={true}
                                    dropdownMode="select"
                                    validations={{
                                      required: 'Bitte füllen Sie dieses Feld aus.',
                                      validate: value => {
                                        if (dayjs(watchFaf1Eingezogen).isBefore(dayjs())) {
                                          return 'Aufgrund von Fristen können wir Ihren Umzug nur anmelden, wenn dieser in der Zukunft liegt. Sind Sie bereits umgezogen, wählen Sie bitte "Ich möchte den Lieferanten wechseln aus".'
                                        }
                                        return true
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                          )}
                          <FormRadioItem
                            name="vendor-faf-1-wechselgrund"
                            id="vendor-faf-1-lieferantwechsel"
                            label="Ich möchte den Lieferanten wechseln."
                            value="vendor-faf-1-lieferantwechsel"
                            register={register}
                            errors={errors}
                            defaultChecked={customerInputValues['p-vendor']['vendor-faf-1-wechselgrund'] === 'vendor-faf-1-lieferantwechsel'}
                          />
                          {watchFaf1RadioItems === 'vendor-faf-1-lieferantwechsel' && (
                            <div className="w-full">
                              <div className="flex flex-wrap">
                                <FormCheck
                                  id="vendor-faf-1-kuendigungSonder"
                                  label="Ich habe bereits bei meinem bisherigen Stromlieferanten gekündigt."
                                  className="w-full pt-4 px-4"
                                  register={register}
                                  errors={errors}
                                  defaultChecked={false}
                                />
                                {watchFaf1AdditionalInputs && (
                                  <div className="w-full px-4">
                                    <div className="flex flex-wrap -mx-4">
                                      <FormInput
                                        errors={errors}
                                        id="vendor-faf-1-kuendigungSonder-vertragspartner"
                                        label="Bisheriger Kundenname"
                                        className="w-full sm:w-1/2 px-4"
                                        register={register}
                                        validations={{
                                          required: 'Bitte füllen Sie dieses Feld aus.'
                                        }}
                                      />
                                      <FormDate
                                        errors={errors}
                                        customMessage={(dayjs(watchFaf1Kuendigung).isBefore(dayjs()))
                                          ? 'Aufgrund von Fristen ist ein rückwirkender Wechsel nicht möglich. Wir werden Sie schnellstmöglich in Belieferung bringen.'
                                          : ''
                                        }
                                        register={register}
                                        id="vendor-faf-1-kuendigungSonderDatum"
                                        control={control}
                                        label="Gekündigt zum"
                                        className="w-full sm:w-1/2 px-4"
                                        peekNextMonth={true}
                                        showMonthDropdown={true}
                                        showYearDropdown={true}
                                        dropdownMode="select"
                                        validations={{
                                          required: 'Bitte füllen Sie dieses Feld aus.'
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="w-full md:w-1/2 px-4">
                      {packageFaF2Details && (
                        <div>
                          <h3 className="h5 pt-4 pb-10">
                            2. zusätzliche Abnahmestelle
                          </h3>
                          <AddressWithValidation
                            sliceName="vendorFaf2"
                            prefix="vendor-faf-2"
                            twoRows={true}
                            errors={errors}
                            register={register}
                            watch={watch}
                            control={control}
                            clearErrors={clearErrors}
                            setValue={setValue}
                            initialPostalCode={customerInputValues['p-intro']['intro-faf-2-postleitzahl']}
                            cityOptionsInitial={typeof customerInputValues['p-vendor']['vendor-faf-2-ort'] === 'object' ? [customerInputValues['p-vendor']['vendor-faf-2-ort']] : []}
                            streetOptionsInitial={typeof customerInputValues['p-vendor']['vendor-faf-2-strasse'] === 'object' ? [customerInputValues['p-vendor']['vendor-faf-2-strasse']] : []}
                          />
                          <FormSelectSearchable
                            errors={errors}
                            register={register}
                            control={control}
                            id="vendor-faf-2-anbieter"
                            label="Bisheriger Stromlieferant"
                            placeholder="Auswählen..."
                            options={selectOptions}
                            isSearchable={true}
                            validations={{
                              required: 'Bitte füllen Sie dieses Feld aus.'
                            }}
                          />
                          <FormInput
                            id="vendor-faf-2-vorname"
                            label="Vorname Anschlussnutzer"
                            errors={errors}
                            register={register}
                            validations={{
                              required: 'Bitte füllen Sie dieses Feld aus.'
                            }}
                          />
                          <FormInput
                            id="vendor-faf-2-name"
                            label="Name Anschlussnutzer"
                            errors={errors}
                            register={register}
                            validations={{
                              required: 'Bitte füllen Sie dieses Feld aus.'
                            }}
                          />
                          <div className="flex flex-wrap -mx-4">
                            <FormInput
                              id="vendor-faf-2-zaehlernummer"
                              label="Zählernummer"
                              className="w-full md:w-6/12 px-4"
                              errors={errors}
                              register={register}
                              validations={{
                                minLength: {
                                  value: 5,
                                  message: 'Die Mindestanzahl an Zeichen beträgt 5'
                                },
                                maxLength: {
                                  value: 16,
                                  message: 'Die maximale Zeichenanzahl beträgt 16'
                                },
                                pattern: {
                                  value: /^((?!kommt$)(?!folgt$)(?!später$)(?!nachreichen$))([a-zA-Z0-9äöüÄÖÜß-])*$/,
                                  message: 'Bitte stellen Sie sicher, dass es sich bei Ihrer Eingabe um eine Zählernummer handelt.'
                                },
                                validate: {
                                  validateSerial: (value) => {
                                    const marktlokationsId = watch('vendor-faf-2-marktlokations-id')
                                    if (!value && !marktlokationsId) {
                                      return 'Bitte geben Sie entweder die Zählernummer oder die Marktstandort-ID an.'
                                    }
                                    const waeZaehlernummer = watch('vendor-wae-zaehlernummer')
                                    const waermeZaehlernummer = watch('vendor-waerme-zaehlernummer')
                                    const faf1Zaehlernummer = watch('vendor-faf-1-zaehlernummer')
                                    if (value && (value === waermeZaehlernummer || value === waeZaehlernummer || value === faf1Zaehlernummer)) {
                                      return 'Bitte überprüfen Sie Ihre Angabe. Die Zählernummer wurde bereits innerhalb der Bestellung von Ihnen verwendet. Bitte stellen Sie sicher, dass jede Zählernummer nur einmalig im Bestellprozess angegeben wird.'
                                    } else {
                                      return true
                                    }
                                  }
                                }
                              }}
                            />
                            <FormInput
                              id="vendor-faf-2-vorjahresstromverbrauch"
                              label="(Vorjahres-) Stromverbrauch in KWh"
                              measurement="kWh"
                              className="w-full md:w-6/12 px-4"
                              errors={errors}
                              register={register}
                              disabled={true}
                            />
                          </div>
                          <FormInput
                            id="vendor-faf-2-marktlokations-id"
                            label="Marktlokations-ID"
                            info="Sie finden die Marktlokations-ID in Ihrer letzten Rechnung.
                            Sie dient der eindeutigen Zuordnung von Abnahmestellen, macht den Vertragswechsel eindeutig und damit für Sie schneller."
                            errors={errors}
                            register={register}
                            validations={{
                              minLength: {
                                value: 11,
                                message: 'Bitte tragen Sie Ihre 11-stellige Marktlokations-ID ein.'
                              },
                              maxLength: {
                                value: 11,
                                message: 'Bitte tragen Sie Ihre 11-stellige Marktlokations-ID ein.'
                              },
                              pattern: {
                                value: /^\d+$/,
                                message: 'Bitte geben Sie für die Marktlokations-ID nur Zahlen ein.'
                              },
                              validate: {
                                validateSerial: (value) => {
                                  const zaehlernummer = watch('vendor-faf-2-zaehlernummer')
                                  if (!value && !zaehlernummer) {
                                    return 'Bitte geben Sie entweder die Zählernummer oder die Marktstandort-ID an.'
                                  }
                                  return true
                                }
                              }
                            }}
                          />
                          <div
                            className="react-form__form-group form-group form-group--reduced-spacing form-group--no-outline w-full">
                            <h3 className="h4">Wechselgrund</h3>
                            <div>
                              <FormRadioItem
                                name="vendor-faf-2-wechselgrund"
                                id="vendor-faf-2-neuEingezogen"
                                label="Ich werde umziehen."
                                value="vendor-faf-2-neuEingezogen"
                                register={register}
                                errors={errors}
                                defaultChecked={customerInputValues['p-vendor']['vendor-faf-1-wechselgrund'] === 'vendor-faf-2-neuEingezogen'}
                              />
                              {watchFaf2RadioItems ===
                                'vendor-faf-2-neuEingezogen' && (
                                  <div className="w-full">
                                    <div className="flex flex-wrap">
                                      <FormDate
                                        errors={errors}
                                        register={register}
                                        control={control}
                                        id="vendor-faf-2-neuEingezogenDatum"
                                        label="Datum des Einzugs"
                                        className="w-full sm:w-1/2"
                                        peekNextMonth={true}
                                        showMonthDropdown={true}
                                        showYearDropdown={true}
                                        dropdownMode="select"
                                        validations={{
                                          required: 'Bitte füllen Sie dieses Feld aus.',
                                          validate: value => {
                                            if (dayjs(watchFaf2Eingezogen).isBefore(dayjs())) {
                                              return 'Aufgrund von Fristen können wir Ihren Umzug nur anmelden, wenn dieser in der Zukunft liegt. Sind Sie bereits umgezogen, wählen Sie bitte "Ich möchte den Lieferanten wechseln aus".'
                                            }
                                            return true
                                          }
                                        }}
                                      />
                                    </div>
                                  </div>
                              )}
                              <FormRadioItem
                                name="vendor-faf-2-wechselgrund"
                                id="vendor-faf-2-lieferantwechsel"
                                label="Ich möchte den Lieferanten wechseln."
                                value="vendor-faf-2-lieferantwechsel"
                                register={register}
                                errors={errors}
                                defaultChecked={customerInputValues['p-vendor']['vendor-faf-1-wechselgrund'] === 'vendor-faf-2-lieferantwechsel'}
                              />
                              {watchFaf2RadioItems === 'vendor-faf-2-lieferantwechsel' && (
                                <div className="w-full">
                                  <div className="flex flex-wrap">
                                    <FormCheck
                                      id="vendor-faf-2-kuendigungSonder"
                                      label="Ich habe bereits bei meinem bisherigen Stromlieferanten gekündigt."
                                      className="w-full pt-4 px-4"
                                      register={register}
                                      errors={errors}
                                      defaultChecked={false}
                                    />
                                    {watchFaf2AdditionalInputs && (
                                      <div className="w-full px-4">
                                        <div className="flex flex-wrap -mx-4">
                                          <FormInput
                                            errors={errors}
                                            id="vendor-faf-2-kuendigungSonder-vertragspartner"
                                            label="Bisheriger Kundenname"
                                            className="w-full sm:w-1/2 px-4"
                                            register={register}
                                            validations={{
                                              required: 'Bitte füllen Sie dieses Feld aus.'
                                            }}
                                          />
                                          <FormDate
                                            errors={errors}
                                            customMessage={(dayjs(watchFaf2Kuendigung).isBefore(dayjs()))
                                              ? 'Aufgrund von Fristen ist ein rückwirkender Wechsel nicht möglich. Wir werden Sie schnellstmöglich in Belieferung bringen.'
                                              : ''
                                            }
                                            register={register}
                                            id="vendor-faf-2-kuendigungSonderDatum"
                                            control={control}
                                            label="Gekündigt zum"
                                            className="w-full sm:w-1/2 px-4"
                                            peekNextMonth={true}
                                            showMonthDropdown={true}
                                            showYearDropdown={true}
                                            dropdownMode="select"
                                            validations={{
                                              required: 'Bitte füllen Sie dieses Feld aus.'
                                            }}
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {packageWaerme && (
            <div className="bg-gray-lighter p-6 md:p-10 xl:p-16">
              <div className="flex flex-wrap">
                <div className="w-full">
                  <h2 className="h3">Wärme</h2>
                  <p className="text-sm">
                    Mit dem Zusatzprodukt Wärme können Sie den SENEC-Vorteilstarif für die Versorgung
                    eines Wärmeerzeugers nutzen, beispielsweise einer Wärmepumpe.
                    Voraussetzung ist, dass die Strommenge für den Wärmeerzeuger über einen separaten
                    Zähler erfasst wird.
                  </p>
                </div>
                <div className="w-full mt-4">
                  <div className="flex flex-wrap -mx-4">
                    <FormSelectSearchable
                      errors={errors}
                      register={register}
                      control={control}
                      id="vendor-wae-anbieter"
                      label="Bisheriger Stromlieferant ('Wärmestrom')"
                      placeholder="Auswählen..."
                      info="Bitte nennen Sie uns Ihren bisherigen Stromanbieter für Ihren Wärmestrom.
                      Für die Auswahl eines Energieversorgers aus der Liste können Sie einfach dessen
                      Anfangsbuchstaben eingeben."
                      className="w-full sm:w-1/2 px-4"
                      options={selectOptions}
                      isSearchable={true}
                      validations={{
                        required: 'Bitte füllen Sie dieses Feld aus.'
                      }}
                    />
                    <FormInput
                      id="vendor-wae-vorjahresstromverbrauch"
                      label="Stromverbrauch in kWh"
                      measurement="kWh"
                      disabled={true}
                      info="Hier haben wir Ihren angegebenen Stromverbrauch Ihres Wärmeerzeugers hinterlegt."
                      className="w-full sm:w-1/2 px-4"
                      errors={errors}
                      register={register}
                    />
                    <FormInput
                      id="vendor-wae-zaehlernummer"
                      label="Zählernummer"
                      info="Bitte nennen Sie uns die Zählernummer, über die Ihr Wärmeerzeuger derzeit versorgt wird."
                      className="w-full sm:w-1/2 px-4"
                      errors={errors}
                      register={register}
                      validations={{
                        minLength: {
                          value: 5,
                          message: 'Die Mindestanzahl an Zeichen beträgt 5'
                        },
                        maxLength: {
                          value: 16,
                          message: 'Die maximale Zeichenanzahl beträgt 16'
                        },
                        pattern: {
                          value: /^((?!kommt$)(?!folgt$)(?!später$)(?!nachreichen$))([a-zA-Z0-9äöüÄÖÜß-])*$/,
                          message: 'Bitte stellen Sie sicher, dass es sich bei Ihrer Eingabe um eine Zählernummer handelt.'
                        },
                        validate: {
                          validateSerial: (value) => {
                            const marktlokationsId = watch('vendor-wae-marktlokations-id')
                            if (!value && !marktlokationsId) {
                              return 'Bitte geben Sie entweder die Zählernummer oder die Marktstandort-ID an.'
                            }
                            const waermeZaehlernummer = watch('vendor-waerme-zaehlernummer')
                            const faf1Zaehlernummer = watch('vendor-faf-1-zaehlernummer')
                            const faf2Zaehlernummer = watch('vendor-faf-2-zaehlernummer')
                            if (value && (value === waermeZaehlernummer || value === faf1Zaehlernummer || value === faf2Zaehlernummer)) {
                              return 'Bitte überprüfen Sie Ihre Angabe. Die Zählernummer wurde bereits innerhalb der Bestellung von Ihnen verwendet. Bitte stellen Sie sicher, dass jede Zählernummer nur einmalig im Bestellprozess angegeben wird.'
                            } else {
                              return true
                            }
                          }
                        }
                      }}
                    />
                    <FormInput
                      id="vendor-wae-marktlokations-id"
                      label="Marktlokations-ID"
                      info="Sie finden die Marktlokations-ID in Ihrer letzten Rechnung.
                       Sie dient der eindeutigen Zuordnung von Abnahmestellen, macht den Vertragswechsel eindeutig und damit für Sie schneller."
                      className="w-full sm:w-1/2 px-4"
                      errors={errors}
                      register={register}
                      validations={{
                        minLength: {
                          value: 11,
                          message: 'Bitte tragen Sie Ihre 11-stellige Marktlokations-ID ein.'
                        },
                        maxLength: {
                          value: 11,
                          message: 'Bitte tragen Sie Ihre 11-stellige Marktlokations-ID ein.'
                        },
                        pattern: {
                          value: /^\d+$/,
                          message: 'Bitte geben Sie für die Marktlokations-ID nur Zahlen ein.'
                        },
                        validate: {
                          validateSerial: (value) => {
                            const zaehlernummer = watch('vendor-wae-zaehlernummer')
                            if (!value && !zaehlernummer) {
                              return 'Bitte geben Sie entweder die Zählernummer oder die Marktstandort-ID an.'
                            }
                            return true
                          }
                        }
                      }}
                    />
                    <div
                      className="react-form__form-group form-group form-group--reduced-spacing form-group--no-outline w-full px-4">
                      <h2 className="h4">Wechselgrund</h2>
                      <FormRadioItem
                        name="vendor-wae-wechselgrund"
                        id="vendor-wae-neuEingezogen"
                        label="Ich werde umziehen."
                        value="vendor-wae-neuEingezogen"
                        className="inline-block"
                        register={register}
                        errors={errors}
                        defaultChecked={customerInputValues['p-vendor']['vendor-wae-wechselgrund'] === 'vendor-wae-neuEingezogen'}
                      />
                      {watchWaeRadioItems === 'vendor-wae-neuEingezogen' && (
                        <div className="w-full">
                          <div className="flex flex-wrap">
                            <FormDate
                              errors={errors}
                              register={register}
                              id="vendor-wae-neuEingezogenDatum"
                              control={control}
                              label="Datum des Einzugs"
                              className="w-full sm:w-1/2"
                              peekNextMonth={true}
                              showMonthDropdown={true}
                              showYearDropdown={true}
                              dropdownMode="select"
                              validations={{
                                required: 'Bitte füllen Sie dieses Feld aus.',
                                validate: value => {
                                  if (dayjs(watchWaeEingezogen).isBefore(dayjs())) {
                                    return 'Aufgrund von Fristen können wir Ihren Umzug nur anmelden, wenn dieser in der Zukunft liegt. Sind Sie bereits umgezogen, wählen Sie bitte "Ich möchte den Lieferanten wechseln aus".'
                                  }
                                  return true
                                }
                              }}
                            />
                          </div>
                        </div>
                      )}
                      <FormRadioItem
                        name="vendor-wae-wechselgrund"
                        id="vendor-wae-lieferantwechsel"
                        label="Ich möchte den Lieferanten wechseln."
                        value="vendor-wae-lieferantwechsel"
                        defaultChecked={customerInputValues['p-vendor']['vendor-wae-wechselgrund'] === 'vendor-wae-lieferantwechsel'}
                        register={register}
                        errors={errors}
                      />
                      {watchWaeRadioItems === 'vendor-wae-lieferantwechsel' && (
                        <div className="w-full">
                          <div className="flex flex-wrap">
                            <FormCheck
                              id="vendor-wae-kuendigungSonder"
                              label="Ich habe bereits bei meinem bisherigen Stromlieferanten gekündigt."
                              className="w-full pt-4 px-4"
                              register={register}
                              errors={errors}
                              defaultChecked={false}
                            />
                            {watchWaeAdditionalInputs && (
                              <div className="w-full px-4">
                                <div className="flex flex-wrap -mx-4">
                                  <FormInput
                                    errors={errors}
                                    id="vendor-wae-kuendigungSonder-vertragspartner"
                                    label="Bisheriger Kundenname"
                                    className="w-full sm:w-1/2 px-4"
                                    register={register}
                                    validations={{
                                      required: 'Bitte füllen Sie dieses Feld aus.'
                                    }}
                                  />
                                  <FormDate
                                    errors={errors}
                                    customMessage={(dayjs(watchWaeKuendigung).isBefore(dayjs()))
                                      ? 'Aufgrund von Fristen ist ein rückwirkender Wechsel nicht möglich. Wir werden Sie schnellstmöglich in Belieferung bringen.'
                                      : ''
                                    }
                                    register={register}
                                    id="vendor-wae-kuendigungSonderDatum"
                                    control={control}
                                    label="Gekündigt zum"
                                    className="w-full sm:w-1/2 px-4"
                                    peekNextMonth={true}
                                    showMonthDropdown={true}
                                    showYearDropdown={true}
                                    dropdownMode="select"
                                    validations={{
                                      required: 'Bitte füllen Sie dieses Feld aus.'
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div
            className={`clearfix px-6 px-md-5 px-xl-7 pb-10 ${
              (packageWaerme && packageFaF) ||
              (!packageWaerme && !packageFaF)
                ? 'bg-gray-lighter'
                : 'bg-gray-light'
            }`}
          >
            {prevPage && (
              <qz-button
                class="float-left"
                onClick={handleBack}
              >
                Zurück
              </qz-button>
            )}
            {nextPage && (
              <qz-button
                class="float-right"
                onClick={handleSubmit(onSubmitFn)}
                buttonType="submit"
              >
                Weiter
              </qz-button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default Component
